<template>
  <div>
    <template v-if="step === 1">
      <div>
        <base-label id="daktelaEmailUrl" label="Daktela URL" :is-required="true"/>
        <base-input id="daktelaEmailUrl"
                    name="daktelaEmailUrl"
                    variant="editor"
                    type="text"
                    class="w-full border-gray py-px mt-1 h-10"
                    data-testid="daktela-email-url"
                    placeholder="https://company.daktela.com"
                    v-model.trim="daktelaUrl"
                    @blur="normalizeUrl"
        />
      </div>

      <div class="mt-6">
        <base-label id="daktelaEmailAccessToken" label="Access Token" :is-required="true"/>
        <base-input id="daktelaEmailAccessToken"
                    name="daktelaEmailAccessToken"
                    variant="editor"
                    type="text"
                    class="w-full border-gray py-px mt-1 h-10"
                    data-testid="daktela-email-token"
                    v-model.trim="accessToken"
        />
        <p v-if="!hasExistingCredentials" class="text-sm text-gray-dark mt-2" data-testid="daktela-email-hint">
          Get the user's access token - Go to <strong>Manage ➝ Users ➝ List of Users</strong>, select the user and copy the access token.
        </p>
      </div>

      <p v-if="isLoadingDetails" class="text-sm text-gray-dark mt-4" data-testid="daktela-email-loading">
        Loading queues, statuses and categories from Daktela...
      </p>
      <p v-if="error" role="alert" class="text-sm text-focus-error mt-4" data-testid="daktela-email-error">
        {{ error }}
      </p>
    </template>

    <template v-else>
      <h4 class="font-semibold text-black" data-testid="daktela-email-parameters-heading">Select parameters</h4>

      <div class="mt-6">
        <base-label id="daktelaEmailQueue" label="Queue" :is-required="true"/>
        <queryable-select v-if="queueOptions.length"
                          id="daktelaEmailQueue"
                          :value="queue"
                          :options="queueOptions"
                          overlay-dropdown
                          class="w-full mt-1 h-10"
                          data-testid="daktela-email-queue"
                          @input="queue = $event"
        />
        <p v-else class="text-sm text-focus-error mt-1" data-testid="daktela-email-queue-empty">
          Daktela returned no queues.
        </p>
      </div>

      <div class="mt-6">
        <base-label id="daktelaEmailQueueStatus" label="Queue status" :is-required="queueStatusOptions.length > 0"/>
        <queryable-select v-if="queueStatusOptions.length"
                          id="daktelaEmailQueueStatus"
                          :value="queueStatus"
                          :options="queueStatusOptions"
                          overlay-dropdown
                          class="w-full mt-1 h-10"
                          data-testid="daktela-email-queue-status"
                          @input="queueStatus = $event"
        />
        <p v-else class="text-sm text-gray-dark mt-1">Daktela returned no queue statuses.</p>
      </div>

      <div class="mt-6">
        <base-label id="daktelaEmailTicketCategory" label="Ticket category" :is-required="true"/>
        <queryable-select v-if="ticketCategoryOptions.length"
                          id="daktelaEmailTicketCategory"
                          :value="ticketCategory"
                          :options="ticketCategoryOptions"
                          overlay-dropdown
                          class="w-full mt-1 h-10"
                          data-testid="daktela-email-ticket-category"
                          @input="ticketCategory = $event"
        />
        <p v-else class="text-sm text-focus-error mt-1" data-testid="daktela-email-category-empty">
          Daktela returned no ticket categories.
        </p>
      </div>
    </template>
  </div>
</template>

<script>
import BaseInput from '@/components/base-components/BaseInput.vue'
import BaseLabel from '@/components/base-components/BaseLabel.vue'
import QueryableSelect from '@/components/common/QueryableSelect.vue'
import utilsService from '@/services/amio/utils.service'
import { findDaktelaEmailCredentials } from '@/utils/channel.utils'
import { normalizeDaktelaUrl } from '@/utils/integration.utils'

const EMPTY_DETAILS = Object.freeze({
  queues: [],
  queue_statuses: [],
  ticket_categories: []
})

export default {
  name: 'DaktelaEmailChannelForm',

  components: {
    BaseInput,
    BaseLabel,
    QueryableSelect
  },

  props: {
    existingChannels: {
      type: Array,
      default: () => []
    }
  },

  emits: ['change'],

  data() {
    return {
      step: 1,
      daktelaUrl: '',
      accessToken: '',
      queue: '',
      queueStatus: '',
      ticketCategory: '',
      details: { ...EMPTY_DETAILS },
      error: '',
      isLoadingDetails: false
    }
  },

  computed: {
    hasExistingCredentials() {
      return !!findDaktelaEmailCredentials(this.existingChannels)
    },
    canSubmit() {
      if (this.step === 1) return !!this.daktelaUrl.trim() && !!this.accessToken.trim()
      const needsStatus = this.details.queue_statuses.length > 0
      return !!this.queue && !!this.ticketCategory && (!needsStatus || !!this.queueStatus)
    },
    payload() {
      const body = {
        daktela_url: normalizeDaktelaUrl(this.daktelaUrl),
        access_token: this.accessToken.trim(),
        daktela_queue: this.queue,
        daktela_ticket_category: this.ticketCategory
      }
      if (this.queueStatus) body.daktela_queue_status = this.queueStatus
      return body
    },
    queueOptions() {
      return toSelectOptions(this.details.queues)
    },
    queueStatusOptions() {
      return toSelectOptions(this.details.queue_statuses)
    },
    ticketCategoryOptions() {
      return toSelectOptions(this.details.ticket_categories)
    }
  },

  watch: {
    existingChannels: {
      immediate: true,
      handler() {
        this.applyCredentials()
      }
    },
    daktelaUrl: 'emitState',
    accessToken: 'emitState',
    queue: 'emitState',
    queueStatus: 'emitState',
    ticketCategory: 'emitState',
    step: 'emitState',
    isLoadingDetails: 'emitState'
  },

  mounted() {
    this.emitState()
  },

  methods: {
    applyCredentials() {
      if (this.daktelaUrl || this.accessToken) return
      const credentials = findDaktelaEmailCredentials(this.existingChannels)
      if (!credentials) return
      this.daktelaUrl = credentials.daktelaUrl
      this.accessToken = credentials.accessToken
    },
    normalizeUrl() {
      this.daktelaUrl = normalizeDaktelaUrl(this.daktelaUrl)
    },
    back() {
      this.error = ''
      this.step = 1
    },
    async loadDetails() {
      if (this.isLoadingDetails) return
      this.error = ''
      this.isLoadingDetails = true
      try {
        const details = await utilsService.getDaktelaEmailDetails({
          daktelaUrl: normalizeDaktelaUrl(this.daktelaUrl),
          accessToken: this.accessToken.trim()
        })
        this.details = {
          queues: details.queues || [],
          queue_statuses: details.queue_statuses || [],
          ticket_categories: details.ticket_categories || []
        }
        this.retainSelections()
        this.step = 3
      } catch (err) {
        this.error = (err && err.message) || 'Daktela API call failed'
        this.step = 1
      } finally {
        this.isLoadingDetails = false
      }
    },
    retainSelections() {
      if (!this.details.queues.some(item => item.id === this.queue)) this.queue = ''
      if (!this.details.queue_statuses.some(item => item.id === this.queueStatus)) this.queueStatus = ''
      if (!this.details.ticket_categories.some(item => item.id === this.ticketCategory)) this.ticketCategory = ''
    },
    emitState() {
      this.$emit('change', {
        step: this.step,
        canSubmit: this.canSubmit,
        isLoadingDetails: this.isLoadingDetails,
        payload: this.payload
      })
    }
  }
}

function toSelectOptions(items = []) {
  return items.map(item => ({
    text: item.name,
    value: item.id,
    subtitle: item.name === item.id ? '' : item.id
  }))
}
</script>
